import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, num, rupee, pnlClass, istDateTime, dataReady } from "@/lib/api";
import { Stat, Empty } from "@/components/Stat";
import { SweepPanel } from "@/components/SweepPanel";
import { RecorderPanel } from "@/components/RecorderPanel";
import { VariantLab, WalkForward, StrategyLab } from "@/components/ResearchPanels";

const iso = (d) => d.toISOString().slice(0, 10);
const FIELDS = [
  ["strategy", "or_minutes", "OR minutes"], ["strategy", "ema_fast", "EMA fast"], ["strategy", "ema_slow", "EMA slow"],
  ["strategy", "rsi_long", "RSI long >"], ["strategy", "rsi_short", "RSI short <"], ["risk", "reward_risk", "Reward : risk"],
  ["risk", "est_debit", "Est. debit (pts)"], ["risk", "eff_delta", "Spread delta"], ["risk", "cost_per_trade", "Cost/trade ₹"],
];

const pf = (v) => (v === null || v === undefined ? "—" : v >= 999 ? "∞" : num(v));

export default function BacktestTab({ status }) {
  const today = new Date();
  const [form, setForm] = useState({ from_date: iso(new Date(today - 90 * 864e5)), to_date: iso(today), oos_pct: 30, pricing: "estimated" });
  const [params, setParams] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setParams((prev) => prev ?? r.data));
    api.get("/backtest/latest").then((r) => r.data?.all && setRes(r.data));
  }, []);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/backtest", { ...form, oos_pct: Number(form.oos_pct), ...params });
      setRes(r.data);
      toast.success(`Backtest complete: ${r.data.all.trades} trades`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const runMulti = async () => {
    setBusy(true);
    try {
      const r = await api.post("/backtest/multi-year", null, { params: { years: 3, oos_pct: Number(form.oos_pct) } });
      setRes(r.data);
      toast.success(`3-year backtest: ${r.data.all.trades} trades, OOS PF ${r.data.out_of_sample.profit_factor ?? "—"}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const setP = (sec, k, v) => setParams((p) => ({ ...p, [sec]: { ...p[sec], [k]: Number(v) } }));
  const ready = dataReady(status);
  const free = status?.data_source === "free";
  const apply = async (x) => {
    const saved = (await api.get("/settings")).data;
    const next = { ...saved, strategy: { ...saved.strategy, or_minutes: x.or_minutes, ema_fast: x.ema_fast, ema_slow: x.ema_slow, rsi_long: x.rsi_long, rsi_short: x.rsi_short, adx_min: x.adx_min ?? 0, or_width_min_pct: x.or_width_min_pct ?? 0 } };
    try { const r = await api.put("/settings", next); setParams((p) => ({ ...p, strategy: r.data.strategy })); toast.success(`Applied OR ${x.or_minutes}m · EMA ${x.ema_fast}/${x.ema_slow} · RSI ${x.rsi_long}/${x.rsi_short} to strategy`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid grid-cols-12 gap-3 sm:gap-4 fade-in">
      <form onSubmit={run} data-testid="form-backtest-parameters" className="panel col-span-12 p-4 lg:col-span-3">
        <div className="eyebrow mb-3">Backtest parameters</div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
          <L t="From"><Input data-testid="input-bt-from" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} /></L>
          <L t="To"><Input data-testid="input-bt-to" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} /></L>
          <L t="Spread pricing">
            <select data-testid="select-bt-pricing" value={form.pricing} onChange={(e) => setForm({ ...form, pricing: e.target.value })}
              className="num h-8 w-full rounded-md border border-input bg-slate-900 px-2 text-xs">
              <option value="estimated">Estimated (index × delta)</option>
              <option value="recorded">Recorded option prices</option>
            </select>
          </L>
          <L t="Out-of-sample %"><Input data-testid="input-bt-oos" type="number" value={form.oos_pct} onChange={(e) => setForm({ ...form, oos_pct: e.target.value })} /></L>
          {params && FIELDS.map(([sec, k, t]) => (
            <L key={k} t={t}><Input data-testid={`input-bt-${k}`} type="number" step="any" value={params[sec][k]} onChange={(e) => setP(sec, k, e.target.value)} /></L>
          ))}
        </div>
        <Button data-testid="button-run-backtest" disabled={busy || !ready} className="mt-4 w-full bg-indigo-500 text-white hover:bg-indigo-400">
          {busy ? "Fetching candles…" : "Run backtest"}
        </Button>
        <Button type="button" data-testid="button-multi-year-backtest" disabled={busy || !ready || free} onClick={runMulti} variant="outline" className="mt-2 w-full border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10">
          {busy ? "Running…" : "3-year backtest (one click)"}
        </Button>
        <p className="mt-1 text-[10px] text-slate-500">Last 3 years of Dhan 5-min data, current rules, {form.oos_pct}% held out as out-of-sample.</p>
        {free && <p data-testid="backtest-free-note" className="mt-2 text-[11px] text-amber-400">Free data (Yahoo): 5-minute history covers only the last ~59 days.</p>}
        {!ready && <p data-testid="backtest-no-data-access" className="mt-2 text-[11px] text-amber-400">Your Dhan plan has no Data API subscription (DH-902), so historical candles are blocked.</p>}
        <p className="mt-3 text-[11px] leading-snug text-slate-500">
          <b className="text-slate-300">Estimated</b>: spread P&amp;L from index moves × spread delta, capped by stop and width. <b className="text-slate-300">Recorded</b>: real spread prices from saved option-chain snapshots; signals without a snapshot are skipped.
        </p>
        <div className="mt-4"><RecorderPanel /></div>
      </form>
      <div className="col-span-12 flex flex-col gap-3 lg:col-span-9">
        {!res ? <Empty id="backtest-empty">Run a backtest to see Profit Factor, drawdown, equity curve and trades.</Empty> : <Results res={res} />}
        <SweepPanel range={{ from_date: form.from_date, to_date: form.to_date, oos_pct: Number(form.oos_pct) }} pricing={form.pricing} disabled={!ready} onApply={apply} />
        <VariantLab disabled={!ready || free} />
        <WalkForward disabled={!ready || free} />
        <StrategyLab disabled={!ready || free} />
      </div>
    </div>
  );
}

const L = ({ t, children }) => (
  <label className="block"><span className="eyebrow !text-[10px]">{t}</span><div className="mt-1 [&_input]:num [&_input]:h-8 [&_input]:bg-slate-900 [&_input]:text-xs">{children}</div></label>
);

const Results = ({ res }) => {
  const a = res.all, o = res.out_of_sample, i = res.in_sample;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid="backtest-meta">
        <span className="num">{res.from_date} → {res.to_date} · {res.days} sessions · OOS from {res.oos_start || "—"}</span>
        {res.pricing === "recorded"
          ? <span data-testid="badge-pricing" className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-emerald-400">RECORDED OPTION PRICES{res.skipped ? ` · ${res.skipped} signals skipped (no snapshot)` : ""}</span>
          : <span data-testid="badge-pricing" className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-amber-400">ESTIMATED P&amp;L</span>}
        <span className="num text-[10px] uppercase text-slate-500">source {res.source || "dhan"}</span>
        <span className="ml-auto">run {istDateTime(res.created_at)}</span>
      </div>
      {a.trades === 0 && (
        <p data-testid="backtest-no-trades" className="panel px-4 py-3 text-xs text-amber-400">
          No trades in this run.{res.pricing === "recorded" ? ` No recorded snapshots matched the signals${res.skipped ? ` (${res.skipped} signals skipped)` : ""}. Let the recorder run through market hours first.` : " Try a longer range or looser rules."}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Trades" value={a.trades} sub={`OOS ${o.trades}`} id="stat-total-trades" />
        <Stat label="Win rate" value={`${num(a.win_rate)}%`} id="stat-win-rate" />
        <Stat label="Profit factor" value={pf(a.profit_factor)} cls={a.profit_factor >= 2 ? "up" : "text-slate-50"} sub="target ≥ 2.00" id="stat-profit-factor" />
        <Stat label="Net P&L" value={rupee(a.net_pnl)} cls={pnlClass(a.net_pnl)} id="stat-net-pnl" />
        <Stat label="Max drawdown" value={rupee(a.max_drawdown)} sub={`${num(a.max_drawdown_pct)}% of capital`} id="stat-max-dd" />
        <Stat label="Expectancy" value={rupee(a.expectancy)} cls={pnlClass(a.expectancy)} sub="per trade" id="stat-expectancy" />
      </div>
      <div className="panel overflow-x-auto">
        <table className="dense num w-full" data-testid="table-split-metrics">
          <thead><tr><th>Segment</th><th>Trades</th><th>Win %</th><th>PF</th><th>Net</th><th>Expectancy</th><th>Avg win</th><th>Avg loss</th><th>Max DD</th></tr></thead>
          <tbody>
            {[["In-sample", i], ["Out-of-sample", o], ["All", a]].map(([n, m]) => (
              <tr key={n}><td className="text-slate-300">{n}</td><td>{m.trades}</td><td>{num(m.win_rate)}</td><td>{pf(m.profit_factor)}</td>
                <td className={pnlClass(m.net_pnl)}>{rupee(m.net_pnl)}</td><td className={pnlClass(m.expectancy)}>{rupee(m.expectancy)}</td>
                <td className="up">{rupee(m.avg_win)}</td><td className="down">{rupee(m.avg_loss)}</td><td>{rupee(m.max_drawdown)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel p-3" data-testid="chart-equity-curve">
        <div className="eyebrow mb-2 px-1">Equity curve</div>
        <div className="h-[240px]">
          <ResponsiveContainer>
            <AreaChart data={res.equity}>
              <defs><linearGradient id="eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} minTickGap={40} />
              <YAxis domain={["auto", "auto"]} tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} width={70} />
              <Tooltip contentStyle={{ background: "#121826", border: "1px solid #334155", fontSize: 12 }} />
              <ReferenceLine y={res.risk.capital} stroke="#475569" strokeDasharray="4 4" />
              {res.oos_start && <ReferenceLine x={res.equity.find((e) => e.oos)?.date} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: "OOS", fill: "#F59E0B", fontSize: 10 }} />}
              <Area type="monotone" dataKey="equity" stroke="#818CF8" fill="url(#eq)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="panel max-h-[420px] overflow-auto">
        <table className="dense num w-full" data-testid="table-backtest-trade-log">
          <thead className="sticky top-0 bg-[#0D121F]"><tr><th>#</th><th>Date</th><th>Entry</th><th>Exit</th><th>Side</th><th>{res.pricing === "recorded" ? "Debit" : "Index in"}</th><th>{res.pricing === "recorded" ? "Exit value" : "Index out"}</th><th>Spread pts</th><th>P&amp;L</th><th>Reason</th><th>Set</th></tr></thead>
          <tbody>
            {res.trades.map((t, k) => (
              <tr key={k}>
                <td className="text-slate-500">{k + 1}</td><td>{t.date}{t.strikes && <span className="ml-1 text-slate-500">{t.strikes}</span>}</td>
                <td>{istDateTime(new Date(t.entry_ts * 1000).toISOString()).split(", ")[1]}</td>
                <td>{istDateTime(new Date(t.exit_ts * 1000).toISOString()).split(", ")[1]}</td>
                <td className={t.direction === "LONG" ? "up" : "down"}>{t.direction}</td>
                <td>{num(t.entry)}</td><td>{num(t.exit)}</td><td>{num(t.spread_pts)}</td>
                <td className={pnlClass(t.pnl)}>{rupee(t.pnl)}</td><td className="uppercase text-slate-400">{t.reason}</td>
                <td className={t.oos ? "text-amber-400" : "text-slate-500"}>{t.oos ? "OOS" : "IS"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
